/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import type React from 'react';

import {
  Dimmer,
  DimmerDimmable,
  Header,
  Icon,
  Loader,
  Segment,
  Table,
} from '@mairistem/composants';

import type { Acte, TypeActe } from '@/types';

import ActeTableLine from './ActeTableLine';

interface Props {
  actes: Array<Acte>;
  loading: boolean;
  typeActe: TypeActe;
  selectedActe?: Acte | null;
  onSelected?: (acte: Acte) => void;
}

const ActeTable = (props: Props) => {
  const { actes, loading, onSelected, selectedActe, typeActe } = props;

  let headers: () => React.ReactNode;

  switch (typeActe) {
    case 'naissance':
    case 'decision':
      headers = () => (
        <>
          <Table.HeaderCell content="Date d'événement" />
          <Table.HeaderCell content="Enfant" />
          <Table.HeaderCell content="Parents" />
        </>
      );
      break;
    case 'reconnaissance':
      headers = () => (
        <>
          <Table.HeaderCell content="Enfant" />
          <Table.HeaderCell content="Reconnue par le père" />
          <Table.HeaderCell content="Reconnue par la mère" />
        </>
      );
      break;
    case 'mariage':
      headers = () => (
        <>
          <Table.HeaderCell content="Date d'événement" />
          <Table.HeaderCell>
            1<sup>er</sup> conjoint
          </Table.HeaderCell>
          <Table.HeaderCell>
            2<sup>ème</sup> conjoint
          </Table.HeaderCell>
        </>
      );
      break;
    case 'deces':
      headers = () => (
        <>
          <Table.HeaderCell content="Date d'événement" />
          <Table.HeaderCell content="Défunt" />
          <Table.HeaderCell content="Parents" />
        </>
      );
      break;
    case 'pacs':
      headers = () => (
        <>
          <Table.HeaderCell content="Date d'enregistrement" />
          <Table.HeaderCell>
            1<sup>er</sup> partenaire
          </Table.HeaderCell>
          <Table.HeaderCell>
            2<sup>ème</sup> partenaire
          </Table.HeaderCell>
        </>
      );
      break;
    default:
      headers = () => undefined;
      break;
  }

  return _.isEmpty(actes) && !loading ? (
    <Segment placeholder style={{ height: '100%' }}>
      <Header icon>
        <Icon name="search" />
        Aucun acte à afficher
      </Header>
    </Segment>
  ) : (
    <Table celled selectable>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell content="Année" />
          <Table.HeaderCell content="N° acte" />
          {typeActe !== 'pacs' && (
            <Table.HeaderCell content="Date et heure dressé" />
          )}
          {headers()}
        </Table.Row>
      </Table.Header>
      <DimmerDimmable
        as={Table.Body}
        dimmed={loading}
        {...(loading && { style: { height: 100 } })}
      >
        <Dimmer active={loading} inverted>
          <Loader indeterminate />
        </Dimmer>
        {_.map(actes, (acte) => (
          <ActeTableLine
            key={acte.identifiant}
            typeActe={typeActe}
            acte={acte}
            active={selectedActe === acte}
            onSelected={onSelected}
          />
        ))}
      </DimmerDimmable>
    </Table>
  );
};

export default ActeTable;
